import {
  EditorState,
  Modifier,
  convertToRaw,
  convertFromRaw,
  SelectionState,
  ContentState,
} from "draft-js";
import uuid from "react-native-uuid";

import { DRAFT } from "../constants";
import {
  FONT_FAMILY_WEIGHT_MAPPING,
  CLOSEST_FONT_WEIGHT_OPTIONS,
} from "../constants/document";

export const checkEmptySelection = editorState => {
  const selectionState = editorState.getSelection();
  if (!selectionState.isCollapsed()) {
    const currentContent = editorState.getCurrentContent();

    const startKey = selectionState.getStartKey();
    const endKey = selectionState.getEndKey();

    const start = selectionState.getStartOffset();
    const end = selectionState.getEndOffset();

    // check if there is no selection
    if (startKey === endKey && start === end) {
      return true;
    }

    // check zero selection between two blocks
    if (
      currentContent.getKeyAfter(startKey) === endKey &&
      currentContent.getBlockForKey(startKey).getLength() === start &&
      end === 0
    ) {
      return true;
    }
    return false;
  }
  return true;
};

export const checkSelectedTextHasHeaders = editorState => {
  const selectionState = editorState.getSelection();
  const currentContent = editorState.getCurrentContent();
  const blocksArray = currentContent.getBlocksAsArray();

  const startKey = selectionState.getStartKey();
  const endKey = selectionState.getEndKey();

  const startIndex = blocksArray.findIndex(block => block.key === startKey);
  const endIndex = blocksArray.findIndex(block => block.key === endKey);

  // check index is valid
  if (startIndex === -1 || endIndex === -1) {
    return false;
  }

  let checkFlag = false;
  for (let index = startIndex; index <= endIndex; index += 1) {
    const block = blocksArray[index];
    if (block.getType().startsWith("header")) {
      checkFlag = true;
      break;
    }
  }
  return checkFlag;
};

export const checkSelectedTextHasEntity = (editorState, entityType) => {
  const selectionState = editorState.getSelection();
  const currentContent = editorState.getCurrentContent();
  const blocksArray = currentContent.getBlocksAsArray();

  const startKey = selectionState.getStartKey();
  const endKey = selectionState.getEndKey();

  const startIndex = blocksArray.findIndex(block => block.key === startKey);
  const endIndex = blocksArray.findIndex(block => block.key === endKey);

  // check index is valid
  if (startIndex === -1 || endIndex === -1) {
    return false;
  }

  const start = selectionState.getStartOffset();
  const end = selectionState.getEndOffset();

  let checkFlag = false;
  for (let index = startIndex; index <= endIndex; index += 1) {
    // get block content
    const block = blocksArray[index];

    const sIndex = index === startIndex ? start : 0;
    const eIndex = index === endIndex ? end : block.getLength();

    for (let i = sIndex; i < eIndex; i += 1) {
      const entity = block.getEntityAt(i);
      if (entity) {
        if (
          !entityType ||
          (entityType && currentContent.getEntity(entity).type === entityType)
        ) {
          checkFlag = true;
          break;
        }
      }
    }
    if (checkFlag) {
      checkFlag = true;
    }
  }
  return checkFlag;
};

export const getSelectedText = editorState => {
  const selectionState = editorState.getSelection();
  const currentContent = editorState.getCurrentContent();
  const blocksArray = currentContent.getBlocksAsArray();

  const startKey = selectionState.getStartKey();
  const endKey = selectionState.getEndKey();

  const startIndex = blocksArray.findIndex(block => block.key === startKey);
  const endIndex = blocksArray.findIndex(block => block.key === endKey);

  // check index is valid
  if (startIndex === -1 || endIndex === -1) {
    return false;
  }

  const start = selectionState.getStartOffset();
  const end = selectionState.getEndOffset();

  let selectedText = "";
  // check for all the selected blocks
  for (let index = startIndex; index <= endIndex; index += 1) {
    // get block content
    const block = blocksArray[index];
    const sIndex = index === startIndex ? start : 0;
    const eIndex = index === endIndex ? end : block.getLength();
    const text = block.getText().slice(sIndex, eIndex);
    selectedText = selectedText ? `${selectedText}\n${text}` : text;
  }

  return selectedText;
};

export const replaceTextAndApplyEntity = (
  editorState,
  textToReplace,
  entityData,
  selectedText,
) => {
  if (selectedText === undefined) {
    selectedText = getSelectedText(editorState);
  }

  const currentContent = editorState.getCurrentContent();
  const selection = editorState.getSelection();

  // create entity with the enitytdata
  const contentStateWithEntity = currentContent.createEntity(
    "DYNAMICFIELD",
    "MUTABLE",
    entityData,
  );
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

  const currentFocus = editorState.getSelection().getFocusKey();
  const inlineStyle = editorState.getCurrentInlineStyle(currentFocus);

  // replace the text and apply entity
  const newContentState = Modifier.replaceText(
    currentContent,
    selection,
    textToReplace,
    inlineStyle,
    entityKey,
  );

  // update the selction state for new content state
  let newSelectionState;
  if (selection.isBackward) {
    // create selection state for the entity to be created
    const selectionState = SelectionState.createEmpty();
    newSelectionState = selectionState.merge({
      focusKey: selection.getFocusKey(),
      anchorKey: selection.getFocusKey(),
      focusOffset: selection.getFocusOffset() + textToReplace.length,
      anchorOffset: selection.getFocusOffset() + textToReplace.length,
      isBackward: false,
    });
  } else {
    // create selection state for the entity to be created
    const selectionState = SelectionState.createEmpty();
    newSelectionState = selectionState.merge({
      focusKey: selection.getAnchorKey(),
      anchorKey: selection.getAnchorKey(),
      focusOffset: selection.getAnchorOffset() + textToReplace.length,
      anchorOffset: selection.getAnchorOffset() + textToReplace.length,
      isBackward: false,
    });
  }

  let updatedEditorState = EditorState.set(editorState, {
    currentContent: newContentState,
    selection: newSelectionState,
  });

  const withSpaceEditoState = Modifier.insertText(
    updatedEditorState.getCurrentContent(),
    updatedEditorState.getSelection(),
    " ",
    updatedEditorState.getCurrentInlineStyle(),
  );

  updatedEditorState = EditorState.push(
    updatedEditorState,
    withSpaceEditoState,
    "apply-entity",
  );

  return updatedEditorState;
};

// Calculates Difference between raw & draftjs offset for an entity
// DraftJs represents Emoji as 1 character, but in raw state emojies can be on length 2,3,4
export const getEmojiDiffFromContentState = (
  currentContent,
  block,
  entityID,
  rawOffset,
) => {
  if (currentContent instanceof ContentState) {
    // eslint-disable-next-line no-plusplus
    for (let i = rawOffset; i < block.getLength(); i++) {
      if (
        block.getEntityAt(i) &&
        currentContent.getEntity(block.getEntityAt(i)) &&
        currentContent.getEntity(block.getEntityAt(i)).getData() &&
        currentContent.getEntity(block.getEntityAt(i)).getData().id === entityID
      ) {
        return i - rawOffset;
      }
    }
  }

  return 0;
};

export const removeEntityAndReplaceText = (
  currentContent,
  entityID,
  replaceText = "",
  returnEditorState = false,
  editorState,
  prevCharEntity,
) => {
  // convert contentstate to raw and get entity map and blocks
  const rawContent = convertToRaw(currentContent);
  const { blocks, entityMap } = rawContent;

  // get the entity key to be deleted
  const entityKey = Object.keys(entityMap).find(
    key => entityMap[key].data.id === entityID,
  );

  let entityRange;
  let blockKey;

  const text = !replaceText
    ? entityMap[entityKey].data.selectedText
    : replaceText;

  // get the entity range of entity to be deleted
  blocks.forEach(block => {
    const { entityRanges, key } = block;
    const range = entityRanges.find(e => e.key.toString() === entityKey);
    if (range) {
      entityRange = range;
      blockKey = key;
    }
  });

  // if entity not found, return current content
  if (!(entityRange && blockKey)) {
    return currentContent;
  }

  const block = currentContent.getBlockForKey(blockKey);

  // get the offset and length
  const { offset, length } = entityRange;

  let focusOffset = offset + length;
  let anchorOffset = offset;

  // handle emoji
  // draftjs counts only 1 char for emoji
  // but actually 1 emoji can be of length greater than one
  // We calculate difference between these lengths
  // & add it to offset to get the correct one
  let emojiDiff = 0;
  const dfExtraSpaceOffset =
    prevCharEntity && prevCharEntity.dfExtraSpaceOffset
      ? prevCharEntity.dfExtraSpaceOffset
      : 0;
  if (
    editorState &&
    editorState.getSelection().getEndOffset() - dfExtraSpaceOffset > focusOffset
  ) {
    emojiDiff =
      editorState.getSelection().getEndOffset() -
      dfExtraSpaceOffset -
      focusOffset;
    focusOffset += emojiDiff;
    anchorOffset += emojiDiff;
  } else if (!editorState) {
    emojiDiff = getEmojiDiffFromContentState(
      currentContent,
      block,
      entityID,
      offset,
    );
    focusOffset += emojiDiff;
    anchorOffset += emojiDiff;
  }

  const inlineStyle = block.getInlineStyleAt(focusOffset);

  // create selection state for the entity to be deleted
  const selectionState = SelectionState.createEmpty();
  let updatedSelection = selectionState.merge({
    focusKey: blockKey,
    anchorKey: blockKey,
    focusOffset,
    anchorOffset,
    isBackward: false,
  });

  // replace entity text with actual text and apply no entity
  const newContentState = Modifier.replaceText(
    currentContent,
    updatedSelection,
    text,
    inlineStyle || null,
    null,
  );

  if (returnEditorState) {
    updatedSelection = selectionState.merge({
      focusKey: blockKey,
      anchorKey: blockKey,
      focusOffset: offset + text.length + emojiDiff,
      anchorOffset: offset + text.length + emojiDiff,
      isBackward: false,
    });
    return {
      currentContent: newContentState,
      selectionState: updatedSelection,
    };
  }
  return newContentState;
};

export const checkCursorBetweenEntity = (editorState, entityType) => {
  const selectionState = editorState.getSelection();
  if (selectionState.isCollapsed()) {
    const currentContent = editorState.getCurrentContent();
    const blockKey = selectionState.getStartKey();
    const block = currentContent.getBlockForKey(blockKey);

    const position = selectionState.getAnchorOffset();
    const prevEntity = position !== 0 ? block.getEntityAt(position - 1) : null;
    const afterEntity =
      position !== block.getLength() ? block.getEntityAt(position + 1) : null;
    if (prevEntity && afterEntity && prevEntity === afterEntity) {
      if (
        !entityType ||
        (entityType && currentContent.getEntity(prevEntity).type === entityType)
      ) {
        return true;
      }
    }
    return false;
  }
  return false;
};

export const findEntityRange = (rawContent, entityID) => {
  const { blocks, entityMap } = rawContent;

  // get the entity key to be deleted
  const entityKey = Object.keys(entityMap).find(
    key => entityMap[key].data.id === entityID,
  );

  let entityRange;
  // get the entity range of entity to be deleted
  blocks.forEach(blockk => {
    const { entityRanges } = blockk;
    const range = entityRanges.find(e => e.key.toString() === entityKey);
    if (range) {
      entityRange = range;
    }
  });

  return entityRange;
};

export const previousCharEntity = (editorState, entityType) => {
  const selectionState = editorState.getSelection();
  if (selectionState.isCollapsed()) {
    const currentContent = editorState.getCurrentContent();
    const blockKey = selectionState.getStartKey();
    const block = currentContent.getBlockForKey(blockKey);

    const position = selectionState.getAnchorOffset();
    if (position === 0) {
      return null;
    }

    const entityID = block.getEntityAt(position - 1);
    let entity;
    let dfExtraSpaceOffset = 0;
    if (!entityID && block.getEntityAt(position - 2)) {
      // special case for dyanmic field entity
      const dfEntity = currentContent.getEntity(
        block.getEntityAt(position - 2),
      );
      if (dfEntity && dfEntity.type === "DYNAMICFIELD") {
        entity = dfEntity;
        dfExtraSpaceOffset = 1;
      }
    } else if (entityID) {
      // regular entity
      entity = currentContent.getEntity(entityID);
    }

    if (entity) {
      if (!entity.data.selectedText) {
        const rawContent = convertToRaw(currentContent);
        const { offset, length } = findEntityRange(rawContent, entity.data.id);
        const text = block.text.substring(offset, offset + length);
        entity.data.selectedText = text;
      }

      if (!entityType || (entityType && entity.type === entityType)) {
        return { entity, dfExtraSpaceOffset };
      }
    }
  }
  return null;
};

export const setBlockTypeToBlock = (blockType, blockKey, editorState) => {
  const currentContent =
    editorState instanceof EditorState
      ? editorState.getCurrentContent()
      : editorState;

  const selection =
    editorState instanceof EditorState
      ? editorState.getSelection()
      : SelectionState.createEmpty();

  const blockLen = currentContent.getBlockForKey(blockKey).getLength();
  const updatedSelection = selection.merge({
    anchorKey: blockKey,
    focusKey: blockKey,
    anchorOffset: 0,
    focusOffset: blockLen,
    isBackward: false,
  });
  const updatedContentState = Modifier.setBlockType(
    currentContent,
    updatedSelection,
    blockType,
  );
  return updatedContentState;
};

export const getUpdatedNestedListType = (
  newListType,
  blockKey,
  contentState,
) => {
  const block = contentState.getBlockForKey(blockKey);
  const oldListType = block.getType();
  const newListTypePrefix = newListType.slice(0, newListType.length - 2);
  const oldListTypePrefix = oldListType.slice(0, oldListType.length - 2);
  if (
    newListTypePrefix !== oldListTypePrefix &&
    (newListType.includes("unordered-list") ||
      newListType.includes("ordered-list")) &&
    (oldListType.includes("ordered-list") ||
      oldListType.includes("unordered-list"))
  ) {
    let updateListType = newListType;
    if (oldListType.split("-")[2]) {
      updateListType = `${newListTypePrefix}-${oldListType.split("-")[2]}`;
    }
    return updateListType;
  }
  return newListType;
};

export const getSelectedBlockTypes = editorState => {
  const contentState = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();
  const blockTypes = [];

  if (selectionState.isCollapsed()) {
    const startKey = selectionState.getStartKey();
    const block = contentState.getBlockForKey(startKey);
    blockTypes.push(block.type);
  } else {
    let startKey = selectionState.getStartKey();
    const endKey = contentState.getKeyAfter(selectionState.getEndKey());
    while (startKey !== endKey) {
      const block = contentState.getBlockForKey(startKey);
      blockTypes.push(block.type);
      startKey = contentState.getKeyAfter(startKey);
    }
  }
  return blockTypes;
};

export const setBlockType = (blockType, editorState) => {
  const currentContent = editorState.getCurrentContent();
  const selection = editorState.getSelection();

  const startKey = selection.getStartKey();
  const endKey = selection.getEndKey();
  let updatedContentState = currentContent;
  if (startKey === endKey) {
    // set the new editor state
    updatedContentState = Modifier.setBlockType(
      currentContent,
      selection,
      blockType,
    );
  } else {
    const start = selection.getStartOffset();
    const end = selection.getEndOffset();
    // convert the first block
    let blockLen = currentContent.getBlockForKey(startKey).getLength();
    if (start < blockLen) {
      const updatedSelection = selection.merge({
        anchorKey: startKey,
        focusKey: startKey,
        anchorOffset: start,
        focusOffset: blockLen,
        isBackward: false,
      });
      let updatedType = blockType;
      // Here, converting the old nested list type to new nested list type,
      // for example, if new blockType is "unordered-list-1" and old is a type of "ordered-list-*" then,
      // converting all unordered nested list into new one(ex: ordered-list-* ==> unordered-list-*)
      if (
        blockType.includes("unordered-list") ||
        blockType.includes("ordered-list")
      ) {
        updatedType = getUpdatedNestedListType(
          blockType,
          startKey,
          updatedContentState,
        );
      }

      updatedContentState = Modifier.setBlockType(
        updatedContentState,
        updatedSelection,
        updatedType,
      );
    }

    // convert the middle blocks
    let nextKey = currentContent.getKeyAfter(startKey);
    while (nextKey !== endKey) {
      blockLen = currentContent.getBlockForKey(nextKey).getLength();
      const updatedSelection = selection.merge({
        anchorKey: nextKey,
        focusKey: nextKey,
        anchorOffset: 0,
        focusOffset: blockLen,
        isBackward: false,
      });
      let updatedType = blockType;
      // Here, converting the old nested list type to new nested list type,
      // for example, if new blockType is "unordered-list-1" and old is a type of "ordered-list-*" then,
      // converting all unordered nested list into new one(ex: ordered-list-* ==> unordered-list-*)
      if (
        blockType.includes("unordered-list") ||
        blockType.includes("ordered-list")
      ) {
        updatedType = getUpdatedNestedListType(
          blockType,
          nextKey,
          updatedContentState,
        );
      }
      updatedContentState = Modifier.setBlockType(
        updatedContentState,
        updatedSelection,
        updatedType,
      );
      nextKey = currentContent.getKeyAfter(nextKey);
    }
    // convert the last block
    if (end !== 0) {
      const updatedSelection = selection.merge({
        anchorKey: endKey,
        focusKey: endKey,
        anchorOffset: 0,
        focusOffset: end,
        isBackward: false,
      });
      let updatedType = blockType;
      // Here, converting the old nested list type to new nested list type,
      // for example, if new blockType is "unordered-list-1" and old is a type of "ordered-list-*" then,
      // converting all unordered nested list into new one(ex: ordered-list-* ==> unordered-list-*)
      if (
        blockType.includes("unordered-list") ||
        blockType.includes("ordered-list")
      ) {
        updatedType = getUpdatedNestedListType(
          blockType,
          endKey,
          updatedContentState,
        );
      }
      updatedContentState = Modifier.setBlockType(
        updatedContentState,
        updatedSelection,
        updatedType,
      );
    }
  }

  // set the new editor state
  const updatedEditorState = EditorState.set(editorState, {
    currentContent: updatedContentState,
  });
  return updatedEditorState;
};

export const checkSelectionHasEntity = (editorState, entityType) => {
  const selection = editorState.getSelection();
  if (selection.isCollapsed()) {
    return checkCursorBetweenEntity(editorState, entityType);
  }
  return checkSelectedTextHasEntity(editorState, entityType);
};

export const createHighlightNotes = editorState => {
  const currentContent = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  const key = uuid.v4();
  const contentStateWithEntity = currentContent.createEntity(
    "NOTES",
    "MUTABLE",
    { id: key },
  );
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
  const updatedContent = Modifier.applyEntity(
    currentContent,
    selection,
    entityKey,
  );
  const updatedEditorState = EditorState.set(editorState, {
    currentContent: updatedContent,
  });
  return { updatedEditorState, key };
};
export const setBlockData = (blockData, contentState, blockKey) => {
  // create new selection object
  const selectionState = SelectionState.createEmpty();
  // make force selection for this block to update the block data
  const updatedSelection = selectionState.merge({
    anchorKey: blockKey,
    focusKey: blockKey,
    anchorOffset: 0,
    focusOffset: 0,
    isBackward: false,
  });
  // set block data
  const updatedContentState = Modifier.setBlockData(
    contentState,
    updatedSelection,
    blockData,
  );
  return updatedContentState;
};

export const toggleBlockIndent = (type, contentState, blockKey) => {
  // get the block
  const block = contentState.getBlockForKey(blockKey);

  const blockType = block.getType();
  // check first the type of block. If it is list ( ordered or unordered) the block should be incremented or decreased
  if (blockType.startsWith("unordered-list")) {
    // get the list level
    const listLevel = parseInt(blockType.split("-")[2], 10);
    let newListLevel =
      listLevel + (typeof type === "string" && type === "indent" ? 1 : -1);
    if (newListLevel < 1) {
      newListLevel = 1;
    } else if (newListLevel > 10) {
      newListLevel = 10;
    }
    const newBlockType = `unordered-list-${newListLevel}`;
    return setBlockTypeToBlock(newBlockType, blockKey, contentState);
  }
  if (blockType.startsWith("ordered-list")) {
    // get the list level
    const listLevel = parseInt(blockType.split("-")[2], 10);
    let newListLevel =
      listLevel + (typeof type === "string" && type === "indent" ? 1 : -1);
    if (newListLevel < 1) {
      newListLevel = 1;
    } else if (newListLevel > 10) {
      newListLevel = 10;
    }
    const newBlockType = `ordered-list-${newListLevel}`;
    return setBlockTypeToBlock(newBlockType, blockKey, contentState);
  }
  // get the indent to update
  const indent =
    typeof type === "string" && type === "indent"
      ? DRAFT.DRAFT_TAB_LENGTH
      : -DRAFT.DRAFT_TAB_LENGTH;

  // get block data
  let blockData = block.data;
  // get element style of the block, if any
  const elementStyle = blockData.get("elementStyle");
  // get all the styles
  const styles =
    typeof elementStyle === "string" ? elementStyle.split(";") : [];

  // check if text-align is there or not
  // if there is any, find the index
  const marginLeftIndex = styles.findIndex(style =>
    style.startsWith("margin-left"),
  );

  let marginCount = indent;
  if (marginLeftIndex > -1) {
    marginCount += parseInt(
      styles[marginLeftIndex]
        .split(":")[1]
        .trim()
        .slice(0, -2),
      10,
    );
  }

  // check new margin, it cannot be less than zero or more than a limit
  if (marginCount < 0) {
    marginCount = 0;
  } else if (marginCount > DRAFT.DRAFT_MAX_TAB_LENGTH) {
    marginCount = DRAFT.DRAFT_MAX_TAB_LENGTH;
  }

  if (marginLeftIndex > -1) {
    // if margin-left is present, update that index
    styles[marginLeftIndex] = `margin-left:${marginCount}px`;
  } else {
    // push margin-left
    styles.push(`margin-left:${marginCount}px`);
  }

  // update block data
  blockData = blockData.set("elementStyle", styles.join(";"));

  // set new block data to the block
  const updatedContentState = setBlockData(blockData, contentState, blockKey);
  return updatedContentState;
};

// Editor toolbar check active button method for bold, underline and italic text
export const checkInlineBtnStatus = (editorState, type, metaData) => {
  const selectionState = editorState.getSelection();
  // If there is no selection
  if (selectionState.isCollapsed()) {
    const inlineStyle = editorState.getCurrentInlineStyle();
    let isType = inlineStyle.has(type);

    const editorValue = editorState.getCurrentContent().getPlainText("\u0001");
    if (
      selectionState.getAnchorOffset() === 0 &&
      editorValue === "" &&
      metaData &&
      metaData[type]
    ) {
      isType = metaData[type];
    }

    if (!isType && type === "BOLD") {
      const anchorKey = selectionState.getAnchorKey();
      const contentBlock = editorState
        .getCurrentContent()
        .getBlockForKey(anchorKey);
      if (contentBlock.type.startsWith("header")) {
        return 1;
      }
    }

    return isType;
  }

  // If there is a selection
  const contentState = editorState.getCurrentContent();
  const anchorKey = selectionState.getAnchorKey();
  const focusKey = selectionState.getFocusKey();
  const startOffset = selectionState.getStartOffset();
  const endOffset = selectionState.getEndOffset();
  let isType = 1;

  // If anchor and focus are in the same block
  if (anchorKey === focusKey) {
    const contentBlock = contentState.getBlockForKey(anchorKey);

    if (type === "BOLD" && contentBlock.type.startsWith("header")) {
      return 1;
    }

    const startIndex = startOffset;
    const endIndex = endOffset;
    for (let i = startIndex; i < endIndex; i += 1) {
      const checkType = contentBlock.getInlineStyleAt(i).has(type);
      if (!checkType) {
        isType *= 0;
        break;
      }
    }
    return isType;
  }

  // If anchor and focus are not in the same block
  let startBlockKey = selectionState.getStartKey();
  let endBlockKey = contentState.getKeyAfter(selectionState.getEndKey());
  if (!endBlockKey) endBlockKey = selectionState.getEndKey();

  // Loop through all selected blocks
  while (startBlockKey !== endBlockKey) {
    const currentContentBlock = contentState.getBlockForKey(startBlockKey);
    const currentContentBlockLength = currentContentBlock.getLength();
    let startIndex;
    let endIndex;
    // Calculate the "startIndex" & "endIndex" of
    // "startBlock", "endBlock" & middle blocks
    if (selectionState.getStartKey() === startBlockKey) {
      startIndex = startOffset;
      endIndex = currentContentBlockLength;
    } else if (selectionState.getEndKey() === startBlockKey) {
      startIndex = 0;
      endIndex = endOffset;
    } else {
      startIndex = 0;
      endIndex = currentContentBlockLength;
    }
    for (let i = startIndex; i < endIndex; i += 1) {
      const checkType =
        currentContentBlock.getInlineStyleAt(i).has(type) ||
        (currentContentBlock.type.startsWith("header") && type === "BOLD");
      if (!checkType) {
        isType *= 0;
        break;
      }
    }
    startBlockKey = contentState.getKeyAfter(startBlockKey);
  }
  return isType;
};

// Editor toolbar check active button method for list, unorder list, H1, H2 & Normal Text
export const checkBlockTypeBtnStatus = (editorState, type, metaData) => {
  const contentState = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();
  let startBlockKey = selectionState.getStartKey();
  const endBlockKey = contentState.getKeyAfter(selectionState.getEndKey());
  let isBtnActive;

  // Case 1 :: Check if there is no selection
  if (selectionState.isCollapsed()) {
    // Find the current block
    const currentBlock = contentState.getBlockForKey(startBlockKey);
    isBtnActive = false;
    const editorValue = editorState.getCurrentContent().getPlainText("\u0001");
    if (
      selectionState.getAnchorOffset() === 0 &&
      editorValue === "" &&
      metaData &&
      metaData.BLOCKTYPE === type
    ) {
      isBtnActive = true;
    }
    // Check whether current block is a given "type" or not
    else if (currentBlock && currentBlock.type === type) {
      isBtnActive = true;
    } else if (
      (type === DRAFT.TOOLBAR_BUTTON_TYPES.ORDERED_LIST ||
        type === DRAFT.TOOLBAR_BUTTON_TYPES.UNORDERED_LIST) &&
      currentBlock.type.startsWith(type)
    ) {
      isBtnActive = true;
    } else if (type === "unstyled") {
      isBtnActive = true;
    }
    return isBtnActive;
  }

  // Case 2 :: Check if there is a selection
  isBtnActive = true;
  // Check whether all selected blocks are a given "type" or not
  while (startBlockKey !== endBlockKey) {
    const currentBlock = contentState.getBlockForKey(startBlockKey);
    if (currentBlock) {
      // If the block type is a list
      if (
        type === DRAFT.TOOLBAR_BUTTON_TYPES.ORDERED_LIST ||
        type === DRAFT.TOOLBAR_BUTTON_TYPES.UNORDERED_LIST
      ) {
        if (!currentBlock.type.startsWith(type)) {
          isBtnActive = false;
          break;
        }
      } else if (currentBlock.type !== type) {
        isBtnActive = false;
        break;
      }
    } else {
      isBtnActive = false;
    }
    startBlockKey = contentState.getKeyAfter(startBlockKey);
  }
  return isBtnActive;
};

// Editor toolbar check active button method for left, center and right align
export const checkAlignTypeBtnStatus = (editorState, type) => {
  const contentState = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();
  let startBlockKey = selectionState.getStartKey();
  const endBlockKey = contentState.getKeyAfter(selectionState.getEndKey());

  // Case 1 ::  Check if there is any selection or not
  if (selectionState.isCollapsed()) {
    const currentBlock = contentState.getBlockForKey(startBlockKey);
    let isActive = false;
    // Check whether current block is a give type or not
    if (currentBlock && currentBlock.getData()) {
      const getStyle = currentBlock.getData().get("elementStyle");
      if (getStyle && getStyle.split(";").includes(type)) {
        isActive = true;
      }
    }
    return isActive;
  }

  // Case 2 :: Check whether all selected block are a give type or not
  let isActive = true;
  while (startBlockKey !== endBlockKey) {
    const currentBlock = contentState.getBlockForKey(startBlockKey);
    if (currentBlock && currentBlock.getData()) {
      const getStyle = currentBlock.getData().get("elementStyle");
      if (getStyle) {
        if (!getStyle.split(";").includes(type)) {
          isActive = false;
          break;
        }
      } else {
        isActive = false;
      }
    } else {
      isActive = false;
    }
    startBlockKey = contentState.getKeyAfter(startBlockKey);
  }
  return isActive;
};

export const removeEntity = (section, entity) => {
  const { id } = entity;
  const { type, data } = section;
  if (type === "HTML") {
    const entityRemoved = removeEntityAndReplaceText(data, id);
    const updatedSection = {
      ...section,
      data: entityRemoved,
    };
    return updatedSection;
  }
  if (type === "Table") {
    const { data: tableData } = data;
    let rowID;
    let columnID;
    let sectionData;
    for (let rowIndex = 0; rowIndex < tableData.length; rowIndex += 1) {
      const row = tableData[rowIndex];
      const { data: rowData } = row;
      for (let colIndex = 0; colIndex < rowData.length; colIndex += 1) {
        const { data: cellData } = rowData[colIndex];
        if (cellData && cellData.data && typeof cellData.data === "object") {
          const rawData = convertToRaw(cellData.data);
          const { entityMap } = rawData;
          const findEntity = Object.keys(entityMap).find(entityKey => {
            const entityData = entityMap[entityKey];
            return entityData.data.id === id;
          });
          if (findEntity) {
            rowID = rowIndex;
            columnID = colIndex;
            sectionData = rawData;
            break;
          }
        }
      }
    }
    const entityRemoved = removeEntityAndReplaceText(
      convertFromRaw(sectionData),
      id,
    );

    const updatedCelldata = {
      data: convertToRaw(entityRemoved),
    };
    tableData[rowID].data[columnID].data = updatedCelldata;
    return section;
  }
  return section;
};

export const updateFormFields = (editorState, oldFormFields) => {
  const rawContent = convertToRaw(editorState.getCurrentContent());
  const idList = [];
  // eslint-disable-next-line no-unused-vars
  Object.entries(rawContent.entityMap).forEach(([key, entity]) => {
    if (entity.type === "DYNAMICFIELD") {
      idList.push(entity.data.id);
    }
  });

  if (oldFormFields && oldFormFields.length > 0) {
    const updatedFormFields = oldFormFields.filter(field =>
      idList.includes(field.id[0]),
    );
    return updatedFormFields;
  }

  return oldFormFields;
};

export const getSelectedContentState = editorState => {
  const selectionState = editorState.getSelection();
  const currentContent = editorState.getCurrentContent();

  // Getting the start and end key of selected blocks
  const startKey = selectionState.getStartKey();
  const endKey = selectionState.getEndKey();
  // Getting the start and end offset of selected text
  const startOffset = selectionState.getStartOffset();
  const endOffset = selectionState.getEndOffset();

  const rowContent = convertToRaw(currentContent);
  const { blocks } = rowContent;

  // Getting the blocks of selected text
  let tempBlocks = [];
  // Single selected line case
  if (startKey === endKey) {
    tempBlocks = blocks.filter(block => block.key === startKey);
  }
  // Multi selected lines case
  else {
    const startIndex = blocks.findIndex(block => block.key === startKey);
    const endIndex = blocks.findIndex(block => block.key === endKey);
    tempBlocks = blocks.slice(startIndex, endIndex + 1);
  }

  //  Here updaing the two things, text and inlineStyleRanges
  if (tempBlocks && tempBlocks.length > 0) {
    tempBlocks.forEach((block, index) => {
      // 1. --------- Single line case ---------
      if (startKey === endKey) {
        // Text update
        block.text = block.text.slice(startOffset, endOffset);
        // inlineStyleRanges update
        if (block.inlineStyleRanges && block.inlineStyleRanges.length > 0) {
          let tempInlineStyles = block.inlineStyleRanges.filter(
            inlineStyle =>
              inlineStyle.offset + inlineStyle.length >= startOffset,
          );
          tempInlineStyles = tempInlineStyles.filter(
            inlineStyle => inlineStyle.offset <= endOffset,
          );
          tempInlineStyles.forEach(inlineStyle => {
            if (inlineStyle.offset <= startOffset) {
              inlineStyle.length -= startOffset - inlineStyle.offset;
              inlineStyle.offset = 0;
            } else {
              if (inlineStyle.offset + inlineStyle.length >= endOffset) {
                inlineStyle.length = endOffset - inlineStyle.offset;
              }
              inlineStyle.offset -= startOffset;
            }
          });
          block.inlineStyleRanges = tempInlineStyles;
        }
        // Entity range update
        if (block.entityRanges && block.entityRanges.length > 0) {
          let tempEntityRanges = block.entityRanges.filter(
            entityRange =>
              entityRange.offset + entityRange.length >= startOffset,
          );
          tempEntityRanges = tempEntityRanges.filter(
            entityRange => entityRange.offset <= endOffset,
          );
          tempEntityRanges.forEach(entityRange => {
            if (entityRange.offset <= startOffset) {
              entityRange.length -= startOffset - entityRange.offset;
              entityRange.offset = 0;
            } else {
              if (entityRange.offset + entityRange.length >= endOffset) {
                entityRange.length = endOffset - entityRange.offset;
              }
              entityRange.offset -= startOffset;
            }
          });
          block.entityRanges = tempEntityRanges;
        }
      }
      // 2. --------- Multi lines case ---------
      //  --------- First line case ---------
      else if (index === 0) {
        // Text update
        block.text = block.text.slice(startOffset);
        // inlineStyleRanges update
        if (block.inlineStyleRanges && block.inlineStyleRanges.length > 0) {
          const tempInlineStyles = block.inlineStyleRanges.filter(
            inlineStyle =>
              inlineStyle.offset + inlineStyle.length >= startOffset,
          );
          tempInlineStyles.forEach(inlineStyle => {
            if (inlineStyle.offset <= startOffset) {
              inlineStyle.length -= startOffset - inlineStyle.offset;
              inlineStyle.offset = 0;
            } else {
              inlineStyle.offset -= startOffset;
            }
          });
          block.inlineStyleRanges = tempInlineStyles;
        }

        // Entity range update
        if (block.entityRanges && block.entityRanges.length > 0) {
          const tempEntityRanges = block.entityRanges.filter(
            entityRange =>
              entityRange.offset + entityRange.length >= startOffset,
          );
          tempEntityRanges.forEach(entityRange => {
            if (entityRange.offset <= startOffset) {
              entityRange.length -= startOffset - entityRange.offset;
              entityRange.offset = 0;
            } else {
              entityRange.offset -= startOffset;
            }
          });
          block.entityRanges = tempEntityRanges;
        }
      }
      //  --------- last line case ---------
      else if (index === tempBlocks.length - 1) {
        // Text update
        block.text = block.text.slice(0, endOffset);
        // inlineStyleRanges update
        if (block.inlineStyleRanges && block.inlineStyleRanges.length > 0) {
          const tempInlineStyles = block.inlineStyleRanges.filter(
            inlineStyle => inlineStyle.offset <= endOffset,
          );
          tempInlineStyles.forEach(inlineStyle => {
            if (inlineStyle.offset + inlineStyle.length >= endOffset) {
              inlineStyle.length = endOffset - inlineStyle.offset;
            }
          });
          block.inlineStyleRanges = tempInlineStyles;
        }
        // Entity range update
        if (block.entityRanges && block.entityRanges.length > 0) {
          const tempEntityRanges = block.entityRanges.filter(
            entityRange => entityRange.offset <= endOffset,
          );
          tempEntityRanges.forEach(entityRange => {
            if (entityRange.offset + entityRange.length >= endOffset) {
              entityRange.length = endOffset - entityRange.offset;
            }
          });
          block.entityRanges = tempEntityRanges;
        }
      }
    });
  }
  rowContent.blocks = tempBlocks;
  const copiedContentState = convertFromRaw(rowContent);
  return copiedContentState;
};

export const getSelectedContentInHtmlFormat = () => {
  let html = "";
  if (typeof window.getSelection !== "undefined") {
    const sel = window.getSelection();
    if (sel.rangeCount) {
      const container = document.createElement("div");
      for (let i = 0, len = sel.rangeCount; i < len; i += 1) {
        container.appendChild(sel.getRangeAt(i).cloneContents());
      }
      html = container.innerHTML;
    }
  } else if (typeof document.selection !== "undefined") {
    if (document.selection.type === "Text") {
      html = document.selection.createRange().htmlText;
    }
  }
  return html;
};

// this method is being used to handle the multiples styles, which may
// come as a single string in "style" key(ex: "FONTCOLOR_333333,FONTSIZE_EIGHTEEN")
export const updateContentStateForMultipleStyles = contentState => {
  const rowContent = convertToRaw(contentState);
  let newInlineStyleRanges = [];
  if (rowContent.blocks && rowContent.blocks.length > 0) {
    rowContent.blocks.forEach(block => {
      const { inlineStyleRanges } = block;
      if (Array.isArray(inlineStyleRanges) && inlineStyleRanges.length > 0) {
        inlineStyleRanges.forEach(range => {
          const { style } = range;
          // Check if the "style" key has more than one style with "," separated
          // then break it to the individual styles
          if (
            style &&
            typeof style === "string" &&
            style.split(",") &&
            style.split(",").length > 1
          ) {
            style.split(",").forEach(st => {
              newInlineStyleRanges.push({
                ...range,
                style: st,
              });
            });
          } else {
            newInlineStyleRanges.push({
              ...range,
            });
          }
        });
      }
      block.inlineStyleRanges = [...newInlineStyleRanges];
      newInlineStyleRanges = [];
    });
  }
  return convertFromRaw(rowContent);
};

export const addInlineStyleToCurrentBlock = (
  editorState,
  style,
  styleToRemove,
) => {
  const currentContent = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  const rawContent = convertToRaw(currentContent);

  const { blocks } = rawContent;
  if (blocks && blocks.length > 0) {
    if (selection.isCollapsed()) {
      const blockKey = selection.getAnchorKey();
      blocks.forEach(block => {
        if (block.key === blockKey) {
          const { inlineStyleRanges } = block;
          block.inlineStyleRanges =
            Array.isArray(inlineStyleRanges) && inlineStyleRanges.length > 0
              ? block.inlineStyleRanges
              : [];
          if (Array.isArray(styleToRemove) && styleToRemove.length > 0) {
            styleToRemove.forEach(sty => {
              block.inlineStyleRanges = block.inlineStyleRanges.filter(
                s => !s.style.includes(sty),
              );
            });
          }

          block.inlineStyleRanges.push({
            offset: 0,
            length: block.text.length,
            style,
          });
        }
      });
    } else {
      // update all selected blocks
      const keySet = new Set();
      let startKey = selection.getStartKey();
      // and update till endKey
      const endKey = currentContent.getKeyAfter(selection.getEndKey());
      while (startKey !== endKey) {
        keySet.add(startKey);
        startKey = currentContent.getKeyAfter(startKey);
      }

      blocks.forEach(block => {
        if (keySet.has(block.key)) {
          const { inlineStyleRanges } = block;
          block.inlineStyleRanges =
            Array.isArray(inlineStyleRanges) && inlineStyleRanges.length > 0
              ? block.inlineStyleRanges
              : [];
          if (Array.isArray(styleToRemove) && styleToRemove.length > 0) {
            styleToRemove.forEach(sty => {
              block.inlineStyleRanges = block.inlineStyleRanges.filter(
                s => !s.style.includes(sty),
              );
            });
          }
          block.inlineStyleRanges.push({
            offset: 0,
            length: block.text.length,
            style,
          });
        }
      });
    }
  }

  let updatedContentState = convertFromRaw(rawContent);
  updatedContentState = Modifier.applyInlineStyle(
    updatedContentState,
    selection,
    "",
  );
  const updatedEditorState = EditorState.push(
    editorState,
    updatedContentState,
    "change-inline-style",
  );

  return updatedEditorState;
};

export const removeStyleFromCurrentBlock = (editorState, style) => {
  const currentContent = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  const rawContent = convertToRaw(currentContent);

  const { blocks } = rawContent;
  if (blocks && blocks.length > 0) {
    if (selection.isCollapsed()) {
      const blockKey = selection.getAnchorKey();
      blocks.forEach(block => {
        if (block.key === blockKey) {
          const { inlineStyleRanges } = block;
          if (
            Array.isArray(inlineStyleRanges) &&
            inlineStyleRanges.length > 0
          ) {
            block.inlineStyleRanges = inlineStyleRanges.filter(
              rng => !rng.style.includes(style),
            );
          }
        }
      });
    } else {
      // update all selected blocks
      const keySet = new Set();
      let startKey = selection.getStartKey();
      // and update till endKey
      const endKey = currentContent.getKeyAfter(selection.getEndKey());
      while (startKey !== endKey) {
        keySet.add(startKey);
        startKey = currentContent.getKeyAfter(startKey);
      }

      blocks.forEach(block => {
        if (keySet.has(block.key)) {
          const { inlineStyleRanges } = block;
          if (
            Array.isArray(inlineStyleRanges) &&
            inlineStyleRanges.length > 0
          ) {
            block.inlineStyleRanges = inlineStyleRanges.filter(
              rng => !rng.style.includes(style),
            );
          }
        }
      });
    }
  }

  let updatedContentState = convertFromRaw(rawContent);
  const currentInlineStyle = editorState.getCurrentInlineStyle();
  updatedContentState = Modifier.applyInlineStyle(
    updatedContentState,
    selection,
    "",
  );
  let updatedEditorState = EditorState.push(
    editorState,
    updatedContentState,
    "change-inline-style",
  );
  updatedEditorState = EditorState.setInlineStyleOverride(
    updatedEditorState,
    currentInlineStyle,
  );
  return updatedEditorState;
};

export const getClosestFontWeightStyle = (
  oldFontWeightStyle,
  fontStyle,
  fontValue,
) => {
  const availableOptions = FONT_FAMILY_WEIGHT_MAPPING[fontValue];

  if (availableOptions) {
    let newFontWeightStyle = null;
    let newFontWeight = null;
    const availableClosestOptionStyle =
      CLOSEST_FONT_WEIGHT_OPTIONS[oldFontWeightStyle];
    availableClosestOptionStyle.some(item => {
      const tempSelect = availableOptions.find(
        styleOption => styleOption.style === item,
      );
      if (tempSelect) {
        newFontWeightStyle = tempSelect.style;
        newFontWeight = tempSelect.name;
        return true;
      }
      return false;
    });
    return { newFontWeightStyle, newFontWeight };
  }
  return {};
};

export const removeStyleFromEditorState = (editorState, style) => {
  const currentContent = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  const startKey = selection.getStartKey();
  const endKey = selection.getEndKey();
  const rowContent = convertToRaw(currentContent);
  const { blocks } = rowContent;
  const findKey = {
    key: "",
    findNextKey: false,
  };
  if (blocks && blocks.length > 0) {
    blocks.forEach((block, index) => {
      if (!findKey.key && block.key === startKey) {
        findKey.key = startKey;
        findKey.findNextKey = true;
      }
      if (block.key === findKey.key && findKey.findNextKey) {
        const { inlineStyleRanges } = block;
        if (Array.isArray(inlineStyleRanges) && inlineStyleRanges.length > 0) {
          block.inlineStyleRanges = inlineStyleRanges.filter(
            rng => !rng.style.includes(style),
          );
        }
        findKey.key = blocks[index + 1] ? blocks[index + 1].key : "";
      }
      if (findKey.key && block.key === endKey && startKey !== endKey) {
        findKey.findNextKey = false;
        findKey.key = "";
      }
    });
  }
  let updatedContentState = convertFromRaw(rowContent);

  updatedContentState = Modifier.applyInlineStyle(
    updatedContentState,
    selection,
    "",
  );
  const updatedEditorState = EditorState.push(
    editorState,
    updatedContentState,
    "change-inline-style",
  );

  return updatedEditorState;
};

export const updateSelectionForEntity = (editorState, entityType) => {
  const currentContent = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();

  // Get Anchor and Focus key of the selected state
  const anchorKey = selectionState.getAnchorKey();
  const focusKey = selectionState.getFocusKey();

  let updateSelection = null;
  let isFocusPosHasEntity = false;
  let isAnchorPosHasEntity = false;
  let finalEntityAnchorOffset = null;
  let finalEntityFocusOffset = null;

  //  Anchor position entity details
  const anchorBlock = currentContent.getBlockForKey(anchorKey);
  const anchorOffset = selectionState.getAnchorOffset();
  const anchorEntityID = anchorBlock.getEntityAt(anchorOffset);
  if (anchorEntityID) {
    const anchorEntity = currentContent.getEntity(anchorEntityID);
    if (anchorEntity.type === entityType) {
      isAnchorPosHasEntity = true;
    }
  }

  // Focus position entity details
  const focusBlock = currentContent.getBlockForKey(focusKey);
  const focusOffset = selectionState.getFocusOffset();
  const focusEntityID = focusBlock.getEntityAt(focusOffset);
  if (focusEntityID) {
    const focusEntity = currentContent.getEntity(focusEntityID);
    if (focusEntity.type === entityType) {
      isFocusPosHasEntity = true;
    }
  }

  // Check if both anchor or focus positon has an entity
  if (isAnchorPosHasEntity || isFocusPosHasEntity) {
    let anchorEntityOffset = null;
    let anchorEntityLength = null;
    let focusEntityOffset = null;
    let focusEntityLength = null;
    const rawContent = convertToRaw(currentContent);

    // Getting required offsets for anchor position
    if (isAnchorPosHasEntity) {
      const anchorRawBlock = rawContent.blocks.find(
        block => block.key === anchorKey,
      );
      const anchorRawEntity = anchorRawBlock.entityRanges.find(
        entity =>
          anchorOffset >= entity.offset &&
          anchorOffset <= entity.offset + entity.length,
      );
      if (anchorRawEntity) {
        anchorEntityOffset = anchorRawEntity.offset;
        anchorEntityLength = anchorRawEntity.length;
      }
    }

    // Getting required offsets for focus position
    if (isFocusPosHasEntity) {
      const focusRawBlock = rawContent.blocks.find(
        block => block.key === focusKey,
      );
      const focusRawEntity = focusRawBlock.entityRanges.find(
        entity =>
          focusOffset >= entity.offset &&
          focusOffset <= entity.offset + entity.length,
      );

      if (focusRawEntity) {
        focusEntityOffset = focusRawEntity.offset;
        focusEntityLength = focusRawEntity.length;
      }
    }

    const isBackwardSelection = selectionState.isBackward;
    // CASE 1 :: if anchor position has an entity and focus position doesn't
    if (isAnchorPosHasEntity && !isFocusPosHasEntity) {
      finalEntityAnchorOffset = isBackwardSelection
        ? focusOffset
        : anchorEntityOffset;
      finalEntityFocusOffset = isBackwardSelection
        ? anchorEntityOffset + anchorEntityLength
        : focusOffset;
    }
    // CASE 2 :: if anchor position doesn't has an entity and focus position has
    else if (!isAnchorPosHasEntity && isFocusPosHasEntity) {
      finalEntityAnchorOffset = isBackwardSelection
        ? focusEntityOffset
        : anchorOffset;
      finalEntityFocusOffset = isBackwardSelection
        ? anchorOffset
        : focusEntityOffset + focusEntityLength;
    }
    // CASE 3 :: if both anchor and focus position have an entity
    else if (isAnchorPosHasEntity && isFocusPosHasEntity) {
      finalEntityAnchorOffset = isBackwardSelection
        ? focusEntityOffset
        : anchorEntityOffset;
      finalEntityFocusOffset = isBackwardSelection
        ? anchorEntityOffset + anchorEntityLength
        : focusEntityOffset + focusEntityLength;
    }

    // Update the selection state based on entity offset and entity length
    updateSelection = selectionState.merge({
      focusKey,
      anchorKey,
      anchorOffset: finalEntityAnchorOffset,
      focusOffset: finalEntityFocusOffset,
      isBackward: false,
    });
  }
  return updateSelection;
};

export const checkIfAnchorOrFocusPosHasEntity = (editorState, entityType) => {
  const currentContent = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();

  // Get Anchor and Focus key of the selected state
  const anchorKey = selectionState.getAnchorKey();
  const focusKey = selectionState.getFocusKey();

  let isFocusPosHasEntity = false;
  let isAnchorPosHasEntity = false;

  //  Anchor position entity details
  const anchorBlock = currentContent.getBlockForKey(anchorKey);
  const anchorOffset = selectionState.getAnchorOffset();
  const anchorEntityID = anchorBlock.getEntityAt(anchorOffset);
  if (anchorEntityID) {
    const anchorEntity = currentContent.getEntity(anchorEntityID);
    if (anchorEntity.type === entityType) {
      isAnchorPosHasEntity = true;
    }
  }

  // Focus position entity details
  const focusBlock = currentContent.getBlockForKey(focusKey);
  const focusOffset = selectionState.getFocusOffset();
  const focusEntityID = focusBlock.getEntityAt(focusOffset);
  if (focusEntityID) {
    const focusEntity = currentContent.getEntity(focusEntityID);
    if (focusEntity.type === entityType) {
      isFocusPosHasEntity = true;
    }
  }

  return isAnchorPosHasEntity || isFocusPosHasEntity;
};



// WEBPACK FOOTER //
// ./src/utils/draftUtils.js